// Global imports
import { useState, useCallback } from 'react'
import { useFetcher } from '@remix-run/react'
import { action as bhAction } from '~/routes/api.get-bank-holidays'

// Types
import type { HssBankHolidayItem } from '~/types'

/**
 * Response shape from the bank holidays API route.
 */
type BankHolsResponse =
  | { success: true; bankHolidays: HssBankHolidayItem[] }
  | { success: false; bankHolidays: HssBankHolidayItem[] }

/**
 * useBankHolidays
 *
 * Manages bank holiday fetching and per-postcode caching.
 * Exposes raw fetcher state so the parent hook can orchestrate
 * the pending-request pattern.
 */
const useBankHolidays = () => {
  const {
    submit: bankHolsSubmit,
    data: bankHolsData,
    state: bankHolsState,
  } = useFetcher<typeof bhAction>()

  // Cached bank holidays per postcode
  const [bankHolidays, setBankHolidays] = useState<HssBankHolidayItem[] | null>(null)
  const [bankHolidaysPostcode, setBankHolidaysPostcode] = useState<string | null>(null)

  // Fetch bank holidays for a postcode
  const fetchBankHolidays = useCallback(
    (postcode: string) => {
      bankHolsSubmit(
        { postcode },
        {
          method: 'post',
          encType: 'application/json',
          action: '/api/get-bank-holidays',
        }
      )
    },
    [bankHolsSubmit]
  )

  /**
   * Ensures bank holidays are cached for the given postcode.
   * Returns 'cached' if already available, 'fetching' if a fetch was triggered.
   */
  const ensureBankHolidays = useCallback(
    (postcode: string): 'cached' | 'fetching' => {
      if (bankHolidays && bankHolidaysPostcode === postcode) {
        return 'cached'
      }

      setBankHolidays(null)
      setBankHolidaysPostcode(postcode)
      fetchBankHolidays(postcode)

      return 'fetching'
    },
    [bankHolidays, bankHolidaysPostcode, fetchBankHolidays]
  )

  /**
   * Parses the raw fetcher response into typed bank holiday data.
   * Returns null if the response isn't ready or failed.
   */
  const parseBankHolsResponse = useCallback((): HssBankHolidayItem[] | null => {
    if (!bankHolsData) return null

    const payload = bankHolsData as unknown as BankHolsResponse

    if (!payload.success) return []

    return payload.bankHolidays
  }, [bankHolsData])

  return {
    bankHolidays,
    bankHolidaysPostcode,
    bankHolsState,
    bankHolsData,
    setBankHolidays,
    setBankHolidaysPostcode,
    fetchBankHolidays,
    ensureBankHolidays,
    parseBankHolsResponse,
  }
}

export default useBankHolidays
